<template>
    <div class="klook-schedule">
      <div
        v-for="(items, day) in groupedByDay"
        :key="day"
        class="day-section"
      >
        <div class="day-header">
          <div class="day-pill">{{ formatDay(day) }}</div>
          <div class="day-subtitle">{{ items.length }} stops</div>
        </div>
  
        <!-- Collapsible wrapper with fade -->
        <div class="collapse-wrap" :class="{ expanded: expanded }">
          <div class="collapse-inner">
            <div class="timeline">
              <div
                v-for="(item, idx) in items"
                :key="day + '-' + idx"
                class="timeline-row"
              >
                <!-- Left: time bubble -->
                <div class="time-col">
                  <div class="time-bubble" :class="{ 'is-empty': !hasTime(item) }">
                    <div class="time-main">
                      {{ formatTime(item.start_time) }}
                    </div>
                    <div v-if="item.end_time" class="time-sub">
                      → {{ formatTime(item.end_time) }}
                    </div>
                  </div>
                </div>
  
                <!-- Center: line + dot -->
                <div class="rail-col" aria-hidden="true">
                  <div class="dot" :class="{ 'dot-muted': !hasTime(item) }"></div>
                  <div
                    class="rail"
                    :class="{ 'rail-last': idx === items.length - 1 }"
                  ></div>
                </div>
  
                <!-- Right: card -->
                <div class="card-col">
                  <div class="stop-card">
                    <div class="card-top">
                      <div class="title-row">
                        <div class="stop-title">{{ item.title }}</div>
                        <span v-if="hasTime(item)" class="tag">Scheduled</span>
                        <span v-else class="tag tag-muted">View from bus</span>
                      </div>
  
                      <div v-if="item.description" class="stop-desc">
                        {{ item.description }}
                      </div>
                    </div>
  
                    <div v-if="item.image_url" class="image-wrap">
                      <img
                        class="stop-image"
                        :src="item.image_url"
                        :alt="item.title"
                        loading="lazy"
                      />
                    </div>
  
                    <div v-if="item.supplement" class="supplement">
                      {{ item.supplement }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
  
          <!-- fade overlay -->
          <div v-if="!expanded" class="fade-overlay"></div>
        </div>
  
        <!-- button -->
        <div v-if="!expanded" class="expand-cta">
          <button class="expand-btn" type="button" @click="expanded = true">
            Show full schedule
          </button>
        </div>
      </div>
  
      <!-- Empty state -->
      <div v-if="!normalized.length" class="empty">
        No schedule items yet.
      </div>
    </div>
  </template>
  
  <script>
  export default {
    name: "KlookTourSchedule",
    props: {
      // Pass your JSON array here
      items: {
        type: Array,
        required: true,
        default: () => []
      }
    },
    data() {
      return {
        // NEW
        expanded: false
      };
    },
    computed: {
      normalized() {
        // Ensure predictable fields + keep original order within each day
        return (this.items || []).map((x) => ({
          start_time: x.start_time === null || x.start_time === undefined ? "" : String(x.start_time),
          end_time: x.end_time === null || x.end_time === undefined ? "" : String(x.end_time),
          title: x.title || "",
          description: x.description || "",
          image_url: x.image_url || "",
          day: x.day || "Day1",
          supplement: x.supplement || ""
        }));
      },
      groupedByDay() {
        const groups = {};
        this.normalized.forEach((item) => {
          const key = item.day || "Day1";
          if (!groups[key]) groups[key] = [];
          groups[key].push(item);
        });
  
        // Sort day keys like Day1, Day2, ... if possible
        const sortedKeys = Object.keys(groups).sort((a, b) => {
          const na = this.extractDayNumber(a);
          const nb = this.extractDayNumber(b);
          if (na !== null && nb !== null) return na - nb;
          return a.localeCompare(b);
        });
  
        const ordered = {};
        sortedKeys.forEach((k) => (ordered[k] = groups[k]));
        return ordered;
      }
    },
    methods: {
      extractDayNumber(dayStr) {
        const m = String(dayStr).match(/day\s*(\d+)/i);
        return m ? parseInt(m[1], 10) : null;
      },
      formatDay(dayStr) {
        // "Day1" -> "Day 1"
        const m = String(dayStr).match(/^(day)\s*(\d+)$/i);
        if (m) return `${m[1][0].toUpperCase()}${m[1].slice(1).toLowerCase()} ${m[2]}`;
        return dayStr;
      },
      hasTime(item) {
        const t = (item && item.start_time != null) ? String(item.start_time).trim() : "";
        return t !== "";
      },
      formatTime(value) {
        // In your data, start_time looks like minute offsets (e.g. -10, 0, 35, 60).
        // We display it like: "T-10m", "T+35m", etc. If you later pass actual clock times, it will show as-is.
        const v = value == null ? "" : String(value).trim();
        if (!v) return "—";
  
        // numeric minute offset?
        const n = Number(v);
        if (!Number.isNaN(n) && v.match(/^-?\d+(\.\d+)?$/)) {
          if (n === 0) return "Start";
          if (n < 0) return `T${n}m`; // e.g. T-10m
          return `T+${n}m`; // e.g. T+35m
        }
  
        // otherwise treat as a label/time string
        return v;
      }
    }
  };
  </script>
  
  <style scoped>
  /* Klook-ish look: rounded cards, soft shadows, clean spacing, orange accent */
  .klook-schedule {
    /* CHANGED per request */
    --klook-orange: #fff;
    --klook-orange-soft: #335dff;
  
    --ink: #202124;
    --muted: #6b7280;
    --bg: #ffffff;
    --card: #ffffff;
    --border: rgba(0, 0, 0, 0.08);
    --shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    --shadow-soft: 0 4px 14px rgba(0, 0, 0, 0.06);
  
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue",
      Arial, "Noto Sans", "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
    color: var(--ink);
  }
  
  .day-section {
    margin-bottom: 18px;
  }
  
  .day-header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin: 10px 2px 10px;
  }
  
  .day-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 999px;
    background: var(--klook-orange-soft);
    color: var(--klook-orange);
    font-weight: 700;
    letter-spacing: 0.2px;
    font-size: 14px;
  }
  
  .day-subtitle {
    color: var(--muted);
    font-size: 13px;
  }
  
  .timeline {
    background: transparent;
  }
  
  .timeline-row {
    display: grid;
    grid-template-columns: 110px 22px 1fr;
    gap: 12px;
    align-items: flex-start;
    padding: 8px 0;
  }
  
  @media (max-width: 520px) {
    .timeline-row {
      grid-template-columns: 92px 18px 1fr;
      gap: 10px;
    }
  }
  
  /* collapsed + fade behavior */
  .collapse-wrap {
    position: relative;
    overflow: hidden;
    max-height: 460px; /* how much of the top to show */
    transition: max-height 0.5s ease;
  }
  .collapse-wrap.expanded {
    max-height: 9999px;
  }
  .collapse-inner {
    /* keep as a hook; no changes needed */
  }
  .fade-overlay {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 90px;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 1));
    pointer-events: none;
  }
  
  /* button */
  .expand-cta {
    display: flex;
    justify-content: center;
    margin-top: 10px;
  }
  .expand-btn {
    appearance: none;
    border: 0;
    background: var(--klook-orange-soft);
    color: var(--klook-orange);
    font-weight: 800;
    border-radius: 999px;
    padding: 10px 14px;
    box-shadow: var(--shadow-soft);
    cursor: pointer;
    transition: transform 0.12s ease, filter 0.12s ease;
  }
  .expand-btn:hover {
    filter: brightness(0.98);
  }
  .expand-btn:active {
    transform: translateY(1px);
  }
  
  /* Time column */
  .time-col {
    position: sticky; /* feels app-like; safe even if not needed */
    top: 0;
  }
  
  .time-bubble {
    border: 1px solid var(--border);
    background: var(--bg);
    border-radius: 14px;
    padding: 10px 10px;
    box-shadow: var(--shadow-soft);
    min-height: 44px;
  }
  
  .time-bubble.is-empty {
    opacity: 0.75;
  }
  
  .time-main {
    font-weight: 800;
    font-size: 13px;
    color: var(--ink);
  }
  
  .time-sub {
    margin-top: 2px;
    font-size: 12px;
    color: var(--muted);
  }
  
  /* Rail */
  .rail-col {
    position: relative;
    display: flex;
    justify-content: center;
  }
  
  .dot {
    width: 12px;
    height: 12px;
    border-radius: 999px;
    background: var(--klook-orange);
    box-shadow: 0 0 0 5px var(--klook-orange-soft);
    margin-top: 14px;
    z-index: 2;
  }
  
  .dot-muted {
    background: #c7c9cc;
    box-shadow: 0 0 0 5px rgba(199, 201, 204, 0.22);
  }
  
  .rail {
    position: absolute;
    top: 28px;
    width: 2px;
    height: calc(100% - 6px);
    background: rgba(0, 0, 0, 0.08);
    border-radius: 2px;
  }
  
  .rail-last {
    height: 0;
  }
  
  /* Card */
  .stop-card {
    background: var(--card);
    border: 1px solid var(--border);
    border-radius: 18px;
    overflow: hidden;
    box-shadow: var(--shadow);
  }
  
  .card-top {
    padding: 14px 14px 12px;
  }
  
  .title-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
  
  .stop-title {
    font-size: 15px;
    font-weight: 800;
    line-height: 1.25;
    color: var(--ink);
  }
  
  .tag {
    font-size: 11px;
    font-weight: 800;
    color: var(--klook-orange);
    background: var(--klook-orange-soft);
    padding: 5px 9px;
    border-radius: 999px;
    letter-spacing: 0.2px;
  }
  
  .tag-muted {
    color: #5b5f66;
    background: rgba(0, 0, 0, 0.06);
  }
  
  .stop-desc {
    margin-top: 8px;
    font-size: 13px;
    color: #3c4043;
    line-height: 1.45;
    white-space: pre-line; /* respects \r\n */
  }
  
  /* Image */
  .image-wrap {
    padding: 0 14px 14px;
  }
  
  .stop-image {
    width: 100%;
    height: 180px;
    object-fit: cover;
    border-radius: 14px;
    display: block;
    background: #f3f4f6;
  }
  
  @media (max-width: 520px) {
    .stop-image {
      height: 160px;
    }
  }
  
  .supplement {
    padding: 0 14px 14px;
    color: var(--muted);
    font-size: 12.5px;
    line-height: 1.4;
  }
  
  /* Empty */
  .empty {
    margin-top: 14px;
    padding: 14px;
    border: 1px dashed var(--border);
    border-radius: 16px;
    color: var(--muted);
    text-align: center;
  }
  </style>
  